import { useCallback, useEffect, useState } from "react";
import { UserPlus, Trash, Check, X, ShieldOff } from "lucide-react";
import { Select } from "antd";

import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { cn } from "../../utils/tailwindUtils";
import ToastMsg from "../common/ToastMsg";
import useApiMethods from "../../lib/useApiMethods";
import { APPRAISAL_REVIEWERS, EMPLOYEE_PERSONAL_INFO } from "../../constants/api";

const RELATIONSHIPS = [
    { value: "peer", label: "Peer" },
    { value: "skip_level", label: "Skip Level" },
    { value: "cross_functional", label: "Cross Functional" },
    { value: "direct_report", label: "Direct Report" },
];

const STATUS_STYLE = {
    nominated: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300",
    approved: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300",
    submitted: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300",
    declined: "bg-gray-100 text-gray-600 dark:bg-gray-700/40 dark:text-gray-300",
};

const STATUS_LABEL = {
    nominated: "Awaiting approval",
    approved: "Invited",
    submitted: "Submitted",
    declined: "Declined",
};

/**
 * The 360 panel for one appraisal.
 *
 * `mode` decides which actions appear, not which data is shown — the API
 * already masks anonymous reviewers for everyone except HR and the reviewer
 * themselves, so this component renders whatever name it is given.
 *   nominate — the employee building their own panel
 *   approve  — the reporting manager accepting or declining nominations
 *   view     — read-only
 */
const ReviewPanel = ({ appraisalId, mode = "view", onChanged, readOnly = false }) => {
    const { fetch, create, update, remove } = useApiMethods();
    const [rows, setRows] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(false);
    const [busy, setBusy] = useState(false);
    const [pick, setPick] = useState({ reviewer_id: null, relationship: "peer" });
    const [declining, setDeclining] = useState(null);
    const [declineReason, setDeclineReason] = useState("");

    const load = useCallback(async () => {
        if (!appraisalId) return;
        setLoading(true);
        try {
            const res = await fetch(APPRAISAL_REVIEWERS, { appraisal_id: appraisalId });
            setRows(res?.data || []);
        } catch (_) { /* a missing panel is not an error */ }
        setLoading(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appraisalId]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        if (mode !== "nominate") return;
        fetch(EMPLOYEE_PERSONAL_INFO, { drop_down: "True" })
            .then((res) => setEmployees(
                (res?.data || []).filter((e) => e.is_active).map((e) => ({
                    value: e.id,
                    label: e.display_name || `${e.first_name || ""} ${e.last_name || ""}`.trim(),
                }))
            ))
            .catch(() => { /* non-fatal */ });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode]);

    const nominate = async () => {
        if (!pick.reviewer_id) { ToastMsg("error", "Pick a colleague to review you"); return; }
        setBusy(true);
        const res = await create(APPRAISAL_REVIEWERS, { appraisal_id: appraisalId, ...pick });
        setBusy(false);
        if (res?.status === "success") {
            setPick({ reviewer_id: null, relationship: "peer" });
            await load(); onChanged?.();
        }
    };

    const decide = async (row, action, reason) => {
        setBusy(true);
        const res = await update(APPRAISAL_REVIEWERS, { id: row.id, action, reason });
        setBusy(false);
        if (res?.status === "success") {
            setDeclining(null); setDeclineReason("");
            await load(); onChanged?.();
        }
    };

    const withdraw = async (row) => {
        const res = await remove(`${APPRAISAL_REVIEWERS}?id=${row.id}`);
        if (res?.status === "success") { await load(); onChanged?.(); }
    };

    return (
        <div className="bg-card border border-border rounded-lg p-4">
            <h3 className="font-semibold mb-1">360&deg; review panel</h3>
            <p className="text-xs text-muted-foreground mb-3">
                {mode === "nominate"
                    ? "Nominate colleagues to give feedback. Your manager approves the panel before anyone is asked."
                    : mode === "approve"
                        ? "Approve or decline the panel the employee nominated."
                        : "Colleagues asked to give feedback on this appraisal."}
            </p>

            {loading ? (
                <p className="text-sm text-muted-foreground">Loading…</p>
            ) : (
                <ul className="space-y-2 mb-3">
                    {rows.map((r) => (
                        <li key={r.id} className="flex items-center gap-2 flex-wrap text-sm">
                            {/* is_masked comes from the API; an anonymous reviewer is
                                already rendered as "Anonymous" with no id, so there is
                                nothing here to accidentally reveal. */}
                            {r.is_masked && <ShieldOff className="h-3.5 w-3.5 text-muted-foreground shrink-0" />}
                            <span className={cn("font-medium", r.is_masked && "italic text-muted-foreground")}>
                                {r.reviewer?.name}
                            </span>
                            <span className="text-xs text-muted-foreground">
                                {RELATIONSHIPS.find((x) => x.value === r.relationship)?.label || r.relationship}
                            </span>
                            <span className={cn("px-2 py-0.5 text-xs rounded-full",
                                STATUS_STYLE[r.status] || STATUS_STYLE.nominated)}>
                                {STATUS_LABEL[r.status] || r.status}
                            </span>
                            {r.declined_reason && (
                                <span className="text-xs text-muted-foreground w-full">
                                    Declined: {r.declined_reason}
                                </span>
                            )}

                            {mode === "approve" && !readOnly && r.status === "nominated" && (
                                <span className="flex gap-1">
                                    <Button size="sm" variant="ghost" disabled={busy}
                                        onClick={() => decide(r, "approve")}>
                                        <Check className="h-4 w-4 text-green-600" />
                                    </Button>
                                    <Button size="sm" variant="ghost" disabled={busy}
                                        onClick={() => setDeclining(r)}>
                                        <X className="h-4 w-4 text-red-500" />
                                    </Button>
                                </span>
                            )}
                            {mode === "nominate" && !readOnly && r.status === "nominated" && (
                                <Button size="sm" variant="ghost" className="text-red-500"
                                    onClick={() => withdraw(r)}>
                                    <Trash className="h-4 w-4" />
                                </Button>
                            )}
                        </li>
                    ))}
                    {rows.length === 0 && (
                        <li className="text-sm text-muted-foreground">No reviewers yet.</li>
                    )}
                </ul>
            )}

            {declining && (
                <div className="border border-border rounded-md p-3 mb-3">
                    <p className="text-sm mb-2">Why are you declining this nomination?</p>
                    <Input value={declineReason} onChange={(e) => setDeclineReason(e.target.value)}
                        placeholder="Reason (required)" />
                    <div className="flex gap-2 mt-2">
                        <Button size="sm" variant="outline" onClick={() => setDeclining(null)}>Cancel</Button>
                        <Button size="sm" disabled={busy}
                            onClick={() => decide(declining, "decline", declineReason)}>
                            Decline
                        </Button>
                    </div>
                </div>
            )}

            {mode === "nominate" && !readOnly && (
                <div className="flex flex-wrap items-center gap-2">
                    <Select className="min-w-[200px]" size="large" showSearch optionFilterProp="label"
                        placeholder="Colleague" options={employees}
                        value={pick.reviewer_id || undefined}
                        onChange={(v) => setPick({ ...pick, reviewer_id: v })} />
                    <Select className="min-w-[160px]" size="large" options={RELATIONSHIPS}
                        value={pick.relationship}
                        onChange={(v) => setPick({ ...pick, relationship: v })} />
                    <Button size="sm" disabled={busy} onClick={nominate}>
                        <UserPlus className="h-4 w-4 mr-1" /> Nominate
                    </Button>
                    <p className="text-xs text-muted-foreground w-full">
                        Each reviewer decides for themselves whether their name travels
                        with their feedback.
                    </p>
                </div>
            )}
        </div>
    );
};

export default ReviewPanel;
